import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TaskManagerState} from '@features/task-manager/store/task-manager.reducer';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {TimeZoneService} from '@core/services/time-zone.service';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {Observable, Subject} from 'rxjs';
import {TicketOwner, TicketReviewer} from '@features/task-manager/constants/task-manager.constants';
import {TaskDetailComponent} from '@features/task-manager/components/task-detail/task-detail.component';
import {addDays, addMonths, addWeeks} from '@progress/kendo-date-math';
import {skip, take, takeUntil} from 'rxjs/operators';
import {getStatusList} from '@features/task-manager/store/task-manager.selector';
import {DateUtility} from '@shared/utility/date-utility';
import {TaskManagerService} from '@features/task-manager/services/task-manager.service';
import {CommandService} from '@core/services/command.service';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {SnackBarService} from '@core/services/snack-bar.service';
import {MatSelectChange} from '@angular/material/select';

@Component({
    standalone: false,
    selector: 'app-task-sub-ticket-view',
  templateUrl: './task-sub-ticket-view.component.html',
  styleUrls: ['./task-sub-ticket-view.component.scss']
})
export class TaskSubTicketViewComponent implements OnInit, OnDestroy {
  @Input() card: any;
  @Output() updateTaskEvent$: EventEmitter<any> = new EventEmitter();
  startDateSelectionType: string;
  selectionTypes: string[] = ['Fixed', 'Relative'];
  dueDateSelectionType: string;
  numbers = [1, 2, 3, 4, 5, 6, 7];

  subTaskDetailForm: FormGroup;
  taskDetailFormErrors: any;
  userInfo: any;
  isSupportUser = false;
  statusHidden = false;
  isSubTask = false;
  hasPermission = true;
  hasPermissionForChangeStatus = true;
  unsubscribe$: Subject<void> = new Subject<void>();
  dueDateEnabled = false;
  startDateEnabled = false;
  fixedStartDateEnabled = false;
  fixedDueDateEnabled = false;
  relativeDueDateEnabled = false;
  relativeStartDateEnabled = false;

  daysDifferenceType = [
    {
      displayText: 'Day(s)',
      key: 'Days'
    },
    {
      displayText: 'Week(s)',
      key: 'Weeks'
    },
    {
      displayText: 'Month(s)',
      key: 'Months'
    }
  ];

  constructor(private formBuilder: FormBuilder,
              private snackBarService: SnackBarService,
              private taskManagerStore: Store<TaskManagerState>,
              private taskManagerService: TaskManagerService,
              private queryStore: Store<IQueryState>,
              private timeZoneService: TimeZoneService,
              private commandService: CommandService,
              private securityStore: Store<SecurityState>) {
    this.subTaskDetailForm = this.formBuilder.group({
      taskStartDate: [Validators.required],
      dueDate: [],
      relativeStartDate: this.formBuilder.group({
        unit: [],
        period: []
      }),
      relativeDueDate: this.formBuilder.group({
        unit: [],
        period: []
      })
    });
  }

  private calculateDate(unit: number, period: string, date: Date): Date {
    switch (period) {
      case 'days':
        return addDays(date, unit);
      case 'weeks':
        return addWeeks(date, unit);
      case 'months':
        return addMonths(date, unit);
    }
  }


  ngOnInit(): void {
    this.securityStore.pipe(select(getUserInfo)).subscribe((userInfo => {
      this.userInfo = userInfo;
      if (this.userInfo) {
        this.isSupportUser = userInfo.companyId.toLowerCase() === 'orbitaxsupport'; // TODO: Comment left by advise from Masud vai
      }
    }));

    if (this.card) {
      this.isSubTask = this.card?.parentTicket !== null;
      this.startDateEnabled = !!(this.card.startDate);
      this.dueDateEnabled = !!(this.card.dueDate);
      this.setPermission();
    }

    let startDate = (this.card && this.card.taskStartDate) ? this.timeZoneService.getDisplayDate(this.card.taskStartDate) : '';
    if (!this.isValidDate(startDate)) {
      startDate = this.card && this.card.startDate ? this.timeZoneService.getDisplayDate(this.card.startDate) : '';
    }

    // Fixed date is considered as the task start date
    // relative date calculate fixed date based on parent ticket start date
    this.subTaskDetailForm.get('taskStartDate').setValue(startDate);
    this.startDateSelectionType = 'Fixed';
    this.onStartDateSelectionTypeChange({value: this.startDateSelectionType});
    //this.subTaskDetailForm.get('startDateSelectionType').setValue('Fixed');

    const dueDate = this.card && this.card.dueDate ? this.timeZoneService.getDisplayDate(this.card.dueDate) : '';
    this.subTaskDetailForm.get('dueDate').setValue(dueDate);
    this.dueDateSelectionType = 'Fixed';
    this.onDueDateTypeSelectionChange({value: this.dueDateSelectionType});
    //this.subTaskDetailForm.get('dueDateSelectionType').setValue('Fixed');


    if (this.card?.fieldValues) {
      const relativeDate = this.getUnitAndPeriod();
      this.subTaskDetailForm.get('relativeStartDate.unit').setValue(relativeDate['RelativeStartDate']?.unit);
      this.subTaskDetailForm.get('relativeStartDate.period').setValue(relativeDate['RelativeStartDate']?.period);
      this.subTaskDetailForm.get('relativeDueDate.unit').setValue(relativeDate['RelativeDueDate']?.unit);
      this.subTaskDetailForm.get('relativeDueDate.period').setValue(relativeDate['RelativeDueDate']?.period);
    }

    if (this.isSubTask) {
      this.listenFormControlChange('relativeStartDate', 'taskStartDate');
      this.listenFormControlChange('relativeDueDate', 'dueDate');
    }
  }


  private getCalculatedDate(targetControl: string, unit: number, period: string): Date | null {
    switch (targetControl) {
      case 'taskStartDate':
        const startDate = this.card.parentTicket?.startDate;
        if (startDate) {
          const updatedDate =this.calculateDate(unit, period, new Date(startDate));
          const date = this.timeZoneService
            .getDisplayDateFormISO(this.timeZoneService.dateTostringWithoutUTC(updatedDate));
          if (date) {
            this.startDateEnabled = true;
          }
          return date;
        }
        return null;
      case 'dueDate':
        const dueDate = this.card.parentTicket?.dueDate;
        if (dueDate) {
          const updatedDate = this.calculateDate(-unit, period, new Date(dueDate));
          const date = this.timeZoneService
            .getDisplayDateFormISO(this.timeZoneService.dateTostringWithoutUTC(updatedDate));
          if (date) {
            this.dueDateEnabled = true;
          }
          return date;
        }
        return null;
      default:
        return null;
    }
  }

  private listenFormControlChange(control: string, targetControl: string) {
    this.subTaskDetailForm.get(control).valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((values) => {
      if (targetControl === 'taskStartDate' && !this.startDateEnabled) {
        return;
      }
      if (targetControl === 'dueDate' && !this.dueDateEnabled) {
        return;
      }

      const unit = values.unit;
      const period = values.period?.key?.toLowerCase();
      if (unit && period) {
        const date = this.getCalculatedDate(targetControl, unit, period);
        if (date) {
          this.subTaskDetailForm.get(targetControl).setValue(date);
        }
      }
    });
  }

  private getDisplayDate(controlName: string) {
    return this.timeZoneService
      .getDisplayDateFormISO(this.timeZoneService.dateTostringWithoutUTC(this.subTaskDetailForm.get(controlName).value));
  }

  isValidDate(date) {
    return date instanceof Date && !isNaN(date.getTime());
  }

  private getUnitAndPeriod() {
    const fieldValues = this.card.fieldValues.reduce((obj, value) => {
        return {
          ...obj,
          [value.Key]: value.Value
        };
      }
      , {});
    const returnValues = {};
    const relativeStartDate = fieldValues['RelativeStartDate'];
    const relativeDueDate = fieldValues['RelativeDueDate'];

    if (relativeStartDate) {
      this.relativeStartDateEnabled = true;
      this.startDateEnabled = true;
      this.startDateSelectionType = 'Relative';
      this.onStartDateSelectionTypeChange({value: this.startDateSelectionType});
      //this.subTaskDetailForm.get('startDateSelectionType').setValue('Relative');
      const values = relativeStartDate.split(' ');
      const value = {
        unit: parseInt(values[0], 10),
        period: this.getPeriod(values[1].toLowerCase())
      };
      Object.assign(returnValues, {RelativeStartDate: value});
    }
    if (relativeDueDate) {
      this.relativeDueDateEnabled = true;
      this.dueDateEnabled = true;
      this.dueDateSelectionType = 'Relative';
      this.onDueDateTypeSelectionChange({value: this.dueDateSelectionType});
      // this.subTaskDetailForm.get('dueDateSelectionType').setValue('Relative');
      const values = relativeDueDate.split(' ');
      const value = {
        unit: parseInt(values[0], 10),
        period: this.getPeriod(values[1].toLowerCase())
      };
      Object.assign(returnValues, {RelativeDueDate: value});
    }
    return returnValues;
  }

  private getPeriod(period: string): { key: string, displayText: string } {
    switch (period) {
      case 'days':
        return {
          key: 'Days',
          displayText: 'Day(s)'
        };
      case 'weeks':
        return {
          displayText: 'Week(s)',
          key: 'Weeks'
        };
      case 'months':
        return {
          displayText: 'Month(s)',
          key: 'Months'
        };
    }
  }

  private setPermission() {
    this.hasPermission = this.card && this.card.hasPermission;
    this.hasPermissionForChangeStatus = this.card
      && (TicketOwner <= this.card.permissionLevel && this.card.permissionLevel <= TicketReviewer);
  }


  handleStartDateSelectionTypeChange($event: any) {

    this.onFormValuesChanged();

  }


  compareFunction(o1: any, o2: any) {
    return (o1.key === o2.key && o1.displayText === o2.displayText);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  handleDueDateSelectionTypeChange($event: any) {

  }

  onFormValuesChanged() {
    const isSubtask = !!this.card.parentTicket;
    this.card = {
      ...this.card,
      taskStartDate: this.startDateEnabled && this.fixedStartDateEnabled ?
        (DateUtility.isValidDateStringOrObject(this.subTaskDetailForm.get('taskStartDate').value)
          ? this.getDisplayDate('taskStartDate')
          : null)
        : null,
      startDate: this.startDateEnabled && this.fixedStartDateEnabled?
        (DateUtility.isValidDateStringOrObject(this.subTaskDetailForm.get('taskStartDate').value)
          ? this.getDisplayDate('taskStartDate')
          : null)
        : null,
      dueDate: this.dueDateEnabled && this.fixedDueDateEnabled ?
        (DateUtility.isValidDateStringOrObject(this.subTaskDetailForm.get('dueDate').value)
          ? this.getDisplayDate('dueDate')
          : null) :
        null,
      relativeStartDate: this.getRelativeDate(this.startDateEnabled && this.relativeStartDateEnabled, 'relativeStartDate'),
      relativeDueDate: this.getRelativeDate(this.dueDateEnabled && this.relativeDueDateEnabled, 'relativeDueDate'),
    };

    if (DateUtility.isValidDate(this.card.taskStartDate) && DateUtility.isValidDate(this.card.dueDate) &&
      this.card.taskStartDate.getTime() > this.card.dueDate.getTime()) {
      this.snackBarService.showError('Please make sure the due date is not set before the start date.');
    } else {
      this.updateTaskEvent$.emit(this.card);
    }
  }

  private getRelativeDate(enable: boolean, controlName: string): string | null {
    if (!enable) {
      return null;
    }
    const value = this.subTaskDetailForm.get(controlName).value;
    const isNullish = Object.values(value).some(o => o === null || o === undefined);
    return isNullish ? null : `${value.unit.toString()} ${value.period.key}`;
  }

  onStartDateCheckboxClick($event: MatCheckboxChange) {

  }

  onStartStaCheckboxChange($event: MatCheckboxChange) {

    this.startDateEnabled = $event.checked;
    this.onFormValuesChanged();
  }

  onStartDateSelectionTypeChange($event: any, updateTask = false) {
    this.startDateSelectionType = $event.value;
    this.relativeStartDateEnabled = $event.value === 'Relative';
    this.fixedStartDateEnabled = $event.value === 'Fixed';
    if(updateTask) {
      this.onFormValuesChanged();
    }
  }

  onDueDateTypeSelectionChange($event: any, updateTask = false) {
    this.dueDateSelectionType = $event.value;
    this.relativeDueDateEnabled = $event.value === 'Relative';
    this.fixedDueDateEnabled = $event.value === 'Fixed';
    if (updateTask) {
      this.onFormValuesChanged();
    }
  }
}
